import { z } from "zod";
import {
	monedaSchema,
	estadoHipotecaSchema,
	tipoCreditoSchema,
	tipoSeguroSchema,
	periodoTasaSchema,
	tipoBonoSchema,
	optionalNumber,
	requiredPositiveNumber,
	requiredDateString,
	optionalDateString,
	optionalString,
	requiredString,
	timestampFieldsSchema,
	createPaginatedSchema,
} from "@/schemas/common";
import { formatDateISO } from "@/utils/formatters";

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Fields that affect cronograma calculation and summary.
 * When any of these fields change, the cronograma must be regenerated.
 * This matches the backend RELEVANT_FIELDS_FOR_RESUMEN constant.
 */
export const CRONOGRAMA_AFFECTING_FIELDS = new Set([
	"monto_prestamo",
	"tasa_interes",
	"periodo_tasa_interes",
	"plazo_meses",
	"fecha_desembolso",
	"fecha_primera_cuota",
	"dia_pago",
	"cuotas_dobles",
	"meses_cuota_doble",
	"seguro_desgravamen",
	"periodo_seguro_desgravamen",
	"tipo_seguro_desgravamen",
	"fecha_fin_seguro_desgravamen",
	"seguro_bien",
	"periodo_seguro_bien",
	"tipo_seguro_bien",
	"importe_asegurado",
	"fecha_inicio_seguro_bien",
	"portes_mensuales",
	"capitalizar_gracia",
	"bono",
]);

// Cobertura de Seguro de Desgravamen
export const coberturaSeguroDesgravamenSchema = z.enum([
	"individual",
	"endosado",
	"mancumunado",
]);
export type CoberturaSeguroDesgravamen = z.infer<
	typeof coberturaSeguroDesgravamenSchema
>;

// =============================================================================
// MORTGAGE SCHEMAS
// =============================================================================

// Base schema object for extension
const mortgageBaseSchema = z.object({
	nombre: requiredString,
	tipo_credito: tipoCreditoSchema.optional(),
	entidad_financiera: requiredString,
	moneda: monedaSchema,

	// Bonus
	tipo_bono: tipoBonoSchema
		.nullable()
		.transform((val) => val ?? undefined)
		.optional(),
	bono: optionalNumber,

	// Amounts
	inicial: z.coerce
		.number({
			invalid_type_error: "required_number",
			required_error: "required_number",
		})
		.refine((val) => !isNaN(val) && val >= 0, {
			message: "required_positive",
		}),
	monto_prestamo: requiredPositiveNumber,

	// Interest rate
	tasa_interes: requiredPositiveNumber,
	periodo_tasa_interes: periodoTasaSchema,

	// Term and dates
	plazo_meses: z.coerce.number().int("integer_required").min(1, "required_positive"),
	fecha_desembolso: requiredDateString,
	fecha_primera_cuota: requiredDateString,
	dia_pago: optionalNumber,

	// Double payments
	cuotas_dobles: z.boolean().default(false),
	meses_cuota_doble: z.array(z.coerce.number().min(1).max(12)).optional(),

	// Desgravamen insurance
	cobertura_seguro_desgravamen:
		coberturaSeguroDesgravamenSchema.default("individual"),
	seguro_desgravamen: z.coerce.number().default(0),
	periodo_seguro_desgravamen: periodoTasaSchema,
	tipo_seguro_desgravamen: tipoSeguroSchema,
	fecha_inicio_endoso_seguro_desgravamen: optionalDateString,

	// Property insurance
	seguro_bien: z.coerce.number().default(0),
	periodo_seguro_bien: periodoTasaSchema,
	tipo_seguro_bien: tipoSeguroSchema,
	importe_asegurado: optionalNumber,
	fecha_inicio_seguro_bien: optionalDateString,

	// Other charges
	portes_mensuales: z.coerce.number().default(0),

	// Grace period
	capitalizar_gracia: z.boolean().default(false),

	// Status and metadata
	estado: estadoHipotecaSchema.default("activa"),
	id_hipoteca_origen: optionalNumber,
	descripcion: optionalString,
	metadatos: z
		.object({
			base: z.lazy(() => resumenCronogramaSchema).optional(),
			actual: z.lazy(() => resumenCronogramaSchema).optional(),
		})
		.passthrough()
		.optional(),
});

// Exported schema with validations
export const mortgageSchema = mortgageBaseSchema.refine(
	(data) => {
		if (
			data.cuotas_dobles &&
			(!data.meses_cuota_doble || data.meses_cuota_doble.length === 0)
		) {
			return false;
		}
		return true;
	},
	{
		message: "required_if_double_payments",
		path: ["meses_cuota_doble"],
	},
);

export const mortgageResponseSchema = mortgageBaseSchema.extend({
	id: z.number(),
	id_propiedad: z.number(),
	...timestampFieldsSchema.shape,
});

export const mortgagePaginatedSchema = createPaginatedSchema(
	mortgageResponseSchema,
);

// =============================================================================
// CRONOGRAMA SCHEMAS
// =============================================================================

export const cuotaResponseSchema = z.object({
	numero: z.union([z.number(), z.string()]),
	fecha_vencimiento: z.string(),
	saldo_inicial: z.number(),
	interes: z.number(),
	seguro_desgravamen: z.number(),
	seguro_bien: z.number(),
	portes: z.number(),
	amortizacion: z.number(),
	cuota_total: z.number(),
	saldo_final: z.number(),
	dias_periodo: z.number(),
	es_amortizacion_extra: z.boolean(),
	es_cuota_doble: z.boolean(),
	amortizacion_id: z.number().nullable().optional(),
	ahorro_desglose: z
		.object({
			intereses: z.coerce.number(),
			seguro_desgravamen: z.coerce.number(),
			seguro_bien: z.coerce.number(),
			portes: z.coerce.number(),
			total: z.coerce.number(),
		})
		.nullable()
		.optional(),
	tipo_reduccion: z.enum(["cuota", "plazo", "pago_total"]).nullable().optional(),
});

export const capitalizacionGraciaSchema = z.object({
	concepto: z.string(),
	dias: z.number(),
	interes: z.number(),
	seguro_desgravamen: z.number(),
	seguro_bien: z.number(),
	portes: z.number(),
	total_capitalizado: z.number(),
});

export const resumenCronogramaSchema = z.object({
	// Fields from mortgage table (for API response compatibility)
	monto_prestamo: z.number(),
	monto_financiado: z.number(),
	tasa_anual: z.number(),
	plazo_original: z.number(),

	// Calculated summary fields
	capitalizacion_gracia: z.array(capitalizacionGraciaSchema),
	plazo_efectivo: z.number(),
	cuota_mensual: z.number(),
	total_intereses: z.number(),
	total_seguro_desgravamen: z.number(),
	total_seguro_bien: z.number(),
	total_seguros: z.number(),
	total_portes: z.number(),
	total_pagado: z.number(),
	total_capitalizacion: z.number(),
	tcea: z.number(),
	tem: z.number(),
	breakeven_mes: z.union([z.number(), z.string()]).nullable().optional(),
});

export const cronogramaResponseSchema = z.object({
	resumen: resumenCronogramaSchema,
	cronograma: z.array(cuotaResponseSchema),
});

export const cronogramaSimulationRequestSchema = mortgageBaseSchema
	.omit({
		nombre: true,
		estado: true,
		id_hipoteca_origen: true,
		descripcion: true,
		metadatos: true,
	})
	.extend({
		amortizaciones: z
			.array(
				z.object({
					fecha: z.string(),
					monto: z.number(),
					tipo_reduccion: z.enum(["cuota", "plazo", "pago_total"]),
					cuota_mes_pagada: z.boolean(),
				}),
			)
			.optional(),
	});

export const liquidacionHipotecaSchema = z.object({
	fecha_operacion: z.string(),
	dias_devengados: z.number(),
	saldo_capital_pendiente: z.number(),
	interes_devengado: z.number(),
	seguro_desgravamen_devengado: z.number(),
	seguro_bien_devengado: z.number(),
	portes_devengados: z.number(),
	total_pago: z.number(),
});

export const hipotecaTransicionCreateSchema = z.object({
	tipo_transicion: z.enum(["refinanciada", "compra_deuda"]),
	fecha_operacion: z.string(),
	cuota_mes_pagada: z.boolean().default(false),
	descripcion: optionalString,
	nueva_hipoteca: z.lazy(() => mortgageFormSchema),
});

export const transitionFormSchema = z
	.object({
		fecha_operacion: z.string().min(1, "La fecha de operación es obligatoria"),
		cuota_mes_pagada: z.boolean(),
		descripcion: z.string().optional(),
		nueva_hipoteca: z.object({
			nombre: z.string().min(1, "El nombre es obligatorio"),
			entidad_financiera: z.string().min(1, "La entidad es obligatoria"),
			monto_prestamo: z.number().min(0.01, "Debe ser mayor a 0"),
			tasa_interes: z.number().min(0.01, "Debe ser mayor a 0"),
			plazo_meses: z.number().int("integer_required").min(1, "required_positive"),
			fecha_desembolso: z.string().min(1, "Obligatoria"),
			fecha_primera_cuota: z.string().min(1, "Obligatoria"),
			cuotas_dobles: z.boolean(),
			seguro_desgravamen: z.number().min(0, "No puede ser menor que 0"),
			seguro_bien: z.number().min(0, "No puede ser menor que 0"),
			// Advanced fields (editable in the redesigned modal; previously inherited
			// from the source mortgage). All exist on the mortgage schema.
			cobertura_seguro_desgravamen:
				coberturaSeguroDesgravamenSchema.default("individual"),
			tipo_seguro_desgravamen: tipoSeguroSchema.default("porcentaje"),
			periodo_seguro_desgravamen: periodoTasaSchema.default("anual"),
			tipo_seguro_bien: tipoSeguroSchema.default("porcentaje"),
			periodo_seguro_bien: periodoTasaSchema.default("anual"),
			portes_mensuales: z.coerce
				.number()
				.min(0, "No puede ser menor que 0")
				.default(0),
		}),
	})
	.refine(
		(data) =>
			new Date(data.nueva_hipoteca.fecha_primera_cuota) >
			new Date(data.fecha_operacion),
		{
			message: "first_payment_after_disbursement",
			path: ["nueva_hipoteca", "fecha_primera_cuota"],
		},
	);

export type TransitionFormInput = z.infer<typeof transitionFormSchema>;
/**
 * What the form holds before the schema runs. Fields with `.default()` are
 * optional here and required in `TransitionFormInput`, which is why useForm
 * needs both: `useForm<TransitionFormValues, unknown, TransitionFormInput>`.
 */
export type TransitionFormValues = z.input<typeof transitionFormSchema>;

export const hipotecaTransicionResponseSchema = z.object({
	hipoteca_origen: mortgageResponseSchema,
	amortizacion_cierre: z.object({
		id: z.number(),
		id_hipoteca: z.number(),
		tipo_reduccion: z.enum(["cuota", "plazo", "pago_total"]),
		monto: z.number(),
		periodicidad: z.string(),
		fecha: z.string(),
		fecha_fin: z.string().nullable().optional(),
		cuota_mes_pagada: z.boolean(),
		descripcion: z.string().nullable().optional(),
		metadatos: z.record(z.unknown()).optional(),
		created_at: z.string(),
		updated_at: z.string(),
	}),
	liquidacion: liquidacionHipotecaSchema,
	hipoteca_nueva: mortgageResponseSchema,
});

// =============================================================================
// FORM SCHEMA (for react-hook-form)
// =============================================================================

// Form-specific schema with strict validations matching backend
// Omit metadatos since it's readonly and calculated by the backend
export const mortgageFormSchema = mortgageBaseSchema
	.omit({ metadatos: true })
	.refine(
		(data) => {
			if (
				data.cuotas_dobles &&
				(!data.meses_cuota_doble || data.meses_cuota_doble.length === 0)
			) {
				return false;
			}
			return true;
		},
		{
			message: "required_if_double_payments",
			path: ["meses_cuota_doble"],
		},
	)
	.refine(
		(data) => {
			const desembolso = new Date(data.fecha_desembolso);
			const primera = new Date(data.fecha_primera_cuota);
			return primera > desembolso;
		},
		{
			message: "first_payment_after_disbursement",
			path: ["fecha_primera_cuota"],
		},
	)
	.refine(
		(data) => {
			// Individual/Joint coverage requires seguro_desgravamen > 0
			if (
				(data.cobertura_seguro_desgravamen === "individual" ||
					data.cobertura_seguro_desgravamen === "mancumunado") &&
				(data.seguro_desgravamen ?? 0) <= 0
			) {
				return false;
			}
			return true;
		},
		{
			message: "manual_coverage_requires_value",
			path: ["seguro_desgravamen"],
		},
	)
	.refine(
		(data) => {
			// Endorsed coverage requires fecha_inicio_endoso_seguro_desgravamen
			if (
				data.cobertura_seguro_desgravamen === "endosado" &&
				!data.fecha_inicio_endoso_seguro_desgravamen
			) {
				return false;
			}
			return true;
		},
		{
			message: "endosado_requires_fecha_inicio",
			path: ["fecha_inicio_endoso_seguro_desgravamen"],
		},
	)
	.refine(
		(data) => {
			// If seguro_bien is percentage type and > 0, require importe_asegurado
			if (
				data.tipo_seguro_bien === "porcentaje" &&
				(data.seguro_bien ?? 0) > 0 &&
				!data.importe_asegurado
			) {
				return false;
			}
			return true;
		},
		{
			message: "property_insurance_requires_insured_amount",
			path: ["importe_asegurado"],
		},
	)
	.refine(
		(data) => {
			// 2. Validate Bono Consistency
			// If tipo_bono is present and NOT "no_bono", bono must be > 0
			if (data.tipo_bono && data.tipo_bono !== "no_bono") {
				if (!data.bono || data.bono <= 0) {
					return false;
				}
			}
			return true;
		},
		{
			message: "bono_required_for_type",
			path: ["bono"],
		},
	)
	.refine(
		(data) => {
			// If bono > 0, tipo_bono must be present and NOT "no_bono"
			if (data.bono && data.bono > 0) {
				if (!data.tipo_bono || data.tipo_bono === "no_bono") {
					return false;
				}
			}
			return true;
		},
		{
			message: "tipo_bono_required_for_amount",
			path: ["tipo_bono"],
		},
	)
	.refine(
		(data) => {
			if (data.id_hipoteca_origen) return true;
			if (!data.fecha_inicio_seguro_bien) return true;
			const desembolso = new Date(data.fecha_desembolso);
			const entrega = new Date(data.fecha_inicio_seguro_bien);
			return entrega >= desembolso;
		},
		{
			message: "delivery_date_after_disbursement",
			path: ["fecha_inicio_seguro_bien"],
		},
	);

export type MortgageFormData = z.output<typeof mortgageFormSchema>;
export type MortgageFormInput = z.input<typeof mortgageFormSchema>;

// Quick edit schema - only the most commonly edited fields
export const mortgageQuickEditSchema = mortgageBaseSchema.pick({
	nombre: true,
	tasa_interes: true,
	inicial: true,
	plazo_meses: true,
	monto_prestamo: true, // Calculated from property price - inicial
});

export type MortgageQuickEditData = z.output<typeof mortgageQuickEditSchema>;
export type MortgageQuickEditInput = z.input<typeof mortgageQuickEditSchema>;

// =============================================================================
// INFERRED TYPES
// =============================================================================

export type MortgageResponse = z.infer<typeof mortgageResponseSchema>;
export type MortgagePaginated = z.infer<typeof mortgagePaginatedSchema>;

export type CuotaResponse = z.infer<typeof cuotaResponseSchema>;
export type ResumenCronograma = z.infer<typeof resumenCronogramaSchema>;
export type CronogramaResponse = z.infer<typeof cronogramaResponseSchema>;
export type CronogramaSimulationRequest = z.infer<
	typeof cronogramaSimulationRequestSchema
>;
export type LiquidacionHipoteca = z.infer<typeof liquidacionHipotecaSchema>;
export type HipotecaTransicionCreate = z.infer<
	typeof hipotecaTransicionCreateSchema
>;
export type HipotecaTransicionResponse = z.infer<
	typeof hipotecaTransicionResponseSchema
>;

// =============================================================================
// DEFAULT VALUES (Single Source of Truth)
// =============================================================================

export const DEFAULT_MORTGAGE_FORM_VALUES: Partial<MortgageFormInput> = {
	moneda: "PEN",
	tipo_credito: "tradicional",
	tipo_bono: "no_bono",
	bono: 0,
	inicial: 0,
	monto_prestamo: 0,
	tasa_interes: 8.5,
	periodo_tasa_interes: "anual",
	plazo_meses: 240,
	fecha_desembolso: formatDateISO(new Date()),
	fecha_primera_cuota: "",
	dia_pago: 1,
	cuotas_dobles: false,
	meses_cuota_doble: [7, 12],
	cobertura_seguro_desgravamen: "individual",
	seguro_desgravamen: 0.28,
	periodo_seguro_desgravamen: "anual",
	tipo_seguro_desgravamen: "porcentaje",
	fecha_inicio_endoso_seguro_desgravamen: "",
	seguro_bien: 0.28,
	periodo_seguro_bien: "anual",
	tipo_seguro_bien: "porcentaje",
	importe_asegurado: 0,
	fecha_inicio_seguro_bien: formatDateISO(new Date()),
	portes_mensuales: 0,
	capitalizar_gracia: false,
	estado: "activa",
};
